import * as Sentry from '@sentry/vue';
import store from '@/store';

const CustomError = Object.freeze({
  axios: 'AxiosError',
});

// Available levels are "fatal", "error", "warning", "log", "info", and "debug".
export const captureError = (error, type = 'log') => {
  if (error?.name == CustomError.axios) {
    return;
  }

  Sentry.withScope(function (scope) {
    scope.setLevel(type);

    if (store?.getters) {
      const user = store.getters['dataUser'];
      if (user) {
        scope.setUser({
          id: user.userID,
          email: user.userEmail,
          userFirstname: user.userFirstname,
        });
      }
    }

    Sentry.captureException(new Error(error));
  });
};

export const captureLog = (log, type = 'log', setUser = true, additionalData = {}, customMessage = null) => {
  Sentry.withScope(function (scope) {
    scope.setLevel(type);

    // Configurar usuário se solicitado
    if (setUser) {
      const user = store.getters['dataUser'];
      scope.setUser({
        id: user.userID,
        email: user.userEmail,
        userFirstname: user.userFirstname,
      });
    }

    // Adicionar dados adicionais como contexto
    if (Object.keys(additionalData).length > 0) {
      scope.setContext('additionalData', additionalData);
    }

    // Adicionar tags úteis
    scope.setTag('logType', type);
    scope.setTag('timestamp', new Date().toISOString());

    // Determinar a mensagem final
    let finalMessage;
    if (customMessage) {
      finalMessage = customMessage;
    } else if (typeof log === 'string') {
      finalMessage = log;
    } else if (typeof log === 'object' && log.message) {
      finalMessage = log.message;
    } else if (typeof log === 'object') {
      finalMessage = 'Object logged';
    } else {
      finalMessage = String(log);
    }

    // Adicionar breadcrumb para rastreamento
    scope.addBreadcrumb({
      message: finalMessage,
      level: type,
      data: typeof log === 'object' ? log : { message: log, customMessage },
    });

    // Adicionar objeto como contexto se for objeto
    if (typeof log === 'object') {
      scope.setContext('logObject', log);
    }

    // Capturar o log com mensagem customizada
    // scope.setMessage(finalMessage);
    Sentry.captureMessage(finalMessage, type);
  });
};

// Funções auxiliares para diferentes tipos de logs
export const captureInfo = (log, additionalData = {}, setUser = true, customMessage = null) => {
  captureLog(log, 'info', setUser, additionalData, customMessage);
};

export const captureWarning = (log, additionalData = {}, setUser = true, customMessage = null) => {
  captureLog(log, 'warning', setUser, additionalData, customMessage);
};

export const captureDebug = (log, additionalData = {}, setUser = true, customMessage = null) => {
  captureLog(log, 'debug', setUser, additionalData, customMessage);
};

export const captureFatal = (log, additionalData = {}, setUser = true, customMessage = null) => {
  captureLog(log, 'fatal', setUser, additionalData, customMessage);
};

// Função para capturar logs de API/HTTP
export const captureApiLog = (
  endpoint,
  method,
  statusCode,
  responseData = {},
  requestData = {},
  customMessage = null
) => {
  const defaultMessage = `API Call: ${method} ${endpoint} - Status: ${statusCode}`;
  captureLog(
    responseData,
    'info',
    true,
    {
      endpoint,
      method,
      statusCode,
      responseData,
      requestData,
      timestamp: new Date().toISOString(),
    },
    customMessage || defaultMessage
  );
};

// Função para capturar logs de performance
export const capturePerformanceLog = (operation, duration, additionalMetrics = {}, customMessage = null) => {
  const defaultMessage = `Performance: ${operation} took ${duration}ms`;
  captureLog(
    additionalMetrics,
    'info',
    true,
    {
      operation,
      duration,
      ...additionalMetrics,
      timestamp: new Date().toISOString(),
    },
    customMessage || defaultMessage
  );
};

// Função para capturar logs de navegação
export const captureNavigationLog = (from, to, additionalData = {}, customMessage = null) => {
  const defaultMessage = `Navigation: ${from} -> ${to}`;
  captureLog(
    additionalData,
    'info',
    true,
    {
      from,
      to,
      ...additionalData,
      timestamp: new Date().toISOString(),
    },
    customMessage || defaultMessage
  );
};

// Função para capturar logs com mensagem customizada e formatação avançada
export const captureCustomLog = (message, data = {}, type = 'info', options = {}) => {
  const { setUser = true, additionalData = {}, tags = {}, context = {}, fingerprint = null } = options;

  Sentry.withScope(function (scope) {
    scope.setLevel(type);

    // Configurar usuário se solicitado
    if (setUser) {
      const user = store.getters['dataUser'];
      scope.setUser({
        id: user.userID,
        email: user.userEmail,
        userFirstname: user.userFirstname,
      });
    }

    // Adicionar dados como contexto
    if (Object.keys(data).length > 0) {
      scope.setContext('logData', data);
    }

    // Adicionar dados adicionais
    if (Object.keys(additionalData).length > 0) {
      scope.setContext('additionalData', additionalData);
    }

    // Adicionar contexto customizado
    if (Object.keys(context).length > 0) {
      Object.keys(context).forEach((key) => {
        scope.setContext(key, context[key]);
      });
    }

    // Adicionar tags
    scope.setTag('logType', type);
    scope.setTag('timestamp', new Date().toISOString());
    Object.keys(tags).forEach((key) => {
      scope.setTag(key, tags[key]);
    });

    // Adicionar fingerprint se fornecido
    if (fingerprint) {
      scope.setFingerprint(fingerprint);
    }

    // Adicionar breadcrumb
    scope.addBreadcrumb({
      message,
      level: type,
      data: { ...data, ...additionalData },
    });

    // Capturar a mensagem
    scope.setMessage(message);
    Sentry.captureMessage(message, type);
  });
};
